/**
 * chipColors.ts — single source of truth for every chip/badge/pill color
 * in the app.
 *
 * RULES:
 *  1. Adding a new chip → add ONE entry to ONE of these maps. Never inline
 *     `bg-{color}-100 text-{color}-{700,800}` in JSX outside this file —
 *     EXCEPT when the chip's color is tightly coupled to a non-Tailwind
 *     value in the same component (e.g. a card strip's hex must match the
 *     adjacent chip's hue). In that case the inline class is allowed PROVIDED
 *     it carries an inline comment explaining the locality constraint AND (if
 *     the local value is a hex literal) the file is registered in
 *     `parallelHexAudit.test.ts` so the strip↔chip hue agreement is statically
 *     enforced. Current examples:
 *       - `SubscriptionCard.statusConfig.ACTIVE` — emerald chip MUST match its
 *         emerald strip (`#10b981`); see SubscriptionCard.tsx jsdoc and the
 *         entry in `parallelHexAudit.test.ts`.
 *       - `ImportBasketDialog.Stepper` — `done`/`active` step states share an
 *         emerald hue (bg-emerald-100 / bg-emerald-600) so the series reads as
 *         a cohesive progress; pure Tailwind so no `parallelHexAudit` entry,
 *         but the locality is documented inline at the call site.
 *  2. Changing a hue here propagates everywhere. Snapshot test
 *     (`chipColors.test.ts`) ensures any change requires explicit reviewer
 *     sign-off via `--updateSnapshot`.
 *  3. Pick the right palette for your context:
 *     - CHIP_DENSE    — high-density chips inside tables / modals (bg-100, text-800)
 *     - CHIP_SIDE     — BUY / SELL only (filled pill + ring for visual weight)
 *     - CHIP_METADATA — trading-config pills (NIFTY / WEEKLY / INTRADAY / NRML)
 *     - CHIP_ACCENT   — decorative differentiation on StrategyRowView (no semantic meaning)
 *     - CHIP_CATEGORY — marketplace category taxonomy
 *     - CHIP_RISK     — marketplace risk levels
 *     - CHIP_PILL     — page-level pills (lighter bg-50 + ring)
 *     - BADGE_VARIANT — semantic aliases for the generic <Badge> component
 *
 * If a chip doesn't fit any palette AND doesn't have a documented locality
 * constraint per rule 1: STOP and discuss with design rather than invent a
 * new hue inline.
 */

// ── Tier A — DENSE chips (inside tables, modals, dense rows) ─────────────
//
// Keyed by real backend enum name where applicable (COMPLETE / OPEN /
// TRIGGER_PENDING / REJECTED / CANCELLED) so the lookup map is the same
// shape as the data.
export const CHIP_DENSE = {
  // order / SL / exit status
  COMPLETE:        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  OPEN:            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  TRIGGER_PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  // Claim-in-flight (Phase 6). Neutral grey chip — transient (~1s) state where
  // the backend has claimed the position row but the broker place_order hasn't
  // returned yet. Distinct from TRIGGER_PENDING (yellow) which means "waiting
  // for schedule to fire". Matches SL_STATUS.PENDING_PLACE hue for consistency.
  PENDING_PLACE:   'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  REJECTED:        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  CANCELLED:       'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  EXIT_PENDING:    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  // CLOSED uses bg-gray-200 (one shade darker than CANCELLED's bg-gray-100)
  // so the two render visibly distinct when they sit adjacent (e.g. in
  // StatusBoxRow's six-box ribbon). Semantically CLOSED is "archived /
  // terminal" so the heavier visual weight reads as "fully done" vs
  // CANCELLED's "informational footnote".
  CLOSED:          'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
  TERMINATED:      'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  // feature / entry-source / advanced-leg-feature
  WAT:             'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  ORB:             'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  HEDGE:           'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  RE_ENTRY:        'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  RE_COST:         'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  RE_EXEC_SL:      'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  RE_EXEC_TP:      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  MANUAL_RETRY:    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  MSLC:            'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  TSL:             'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  // UL (underlying-based) feature chip — Phase 6 Baskets-page indicator that
  // this strategy carries any UL SL/TP/TSL/W&T config. Yellow to match the
  // UL_MONITOR_PENDING "Waiting on spot" status hue (via TRIGGER_PENDING),
  // so the visual language for "spot-driven" stays consistent from the
  // strategy card down to the position row.
  UL:              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  // execution-source pills (Entered / Closed / Carry on STBT group headers)
  SOURCE_ENTERED:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  SOURCE_CLOSED:   'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
  SOURCE_CARRY:    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  // chase indicator (entry / SL / exit reprice in flight)
  CHASE:           'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  // marketplace-subscribed basket indicator ("MP" pill rendered next to a
  // basket name on Allocations / Schedule views). Violet to match the
  // existing inline violet basket-name links in ScheduleTable / ProScheduleTable.
  MARKETPLACE:     'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
} as const;

// ── Tier A — SIDE chips (BUY / SELL only) ────────────────────────────────
//
// Use text-700 (not 800) because the ring already adds visual weight; bumping
// to 800 over-saturates the small chip.
export const CHIP_SIDE = {
  BUY:  'bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800',
  SELL: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-800',
} as const;

// ── Tier A — METADATA pills (NIFTY / WEEKLY / INTRADAY / NRML / MIS / etc.) ──
//
// Neutral gray for most; BTST gets indigo accent because it indicates
// cross-day risk and traders need it to stand out at a glance.
export const CHIP_METADATA = {
  DEFAULT: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  BTST:    'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
} as const;

// ── Tier A — DECORATIVE accents (StrategyRowView trading-type / underlying / expiry) ──
//
// Pure visual differentiation, no semantic meaning. Restricted set so we
// don't invent new hues per page.
export const CHIP_ACCENT = {
  TRADING_TYPE: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  UNDERLYING:   'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  EXPIRY:       'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300',
} as const;

// ── Tier A — MARKETPLACE category & risk chips ───────────────────────────
//
// Marketplace cards have their own taxonomy — distinct from status semantics.
// Stay on text-700 here because these are visible at card-list scale (larger
// chips than dense table chips).
//
// ⚠️ Hue MUST match `MarketplaceBasketCard.categoryColors` (the hex map driving
// the card's left-edge strip). Strip + badge are rendered on the same card and
// MUST visually agree. If you change a hue here, update the hex too:
//   INCOME       emerald   #059669   (emerald-600)
//   MOMENTUM     blue      #2563eb   (blue-600)
//   HEDGED       amber     #f59e0b   (amber-500)
//   AGGRESSIVE   purple    #9333ea   (purple-600)
//   CONSERVATIVE emerald   #10b981   (emerald-500)  — shares hue with INCOME by design
export const CHIP_CATEGORY = {
  INCOME:       'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  MOMENTUM:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  HEDGED:       'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  AGGRESSIVE:   'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  CONSERVATIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
} as const;

export const CHIP_RISK = {
  LOW:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  HIGH:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  // Circuit-cap recovery (Phase 2/3): broker's daily circuit limit forced the
  // SL or entry trigger below the user's configured level. Amber matches
  // MEDIUM/CHASE — same "needs attention but not critical" semantic. Recovery
  // retries as bands widen across days, so the chip is informational, not
  // an error.
  CAPPED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
} as const;

// ── Tier B — PAGE-LEVEL pills (LeadStatusPill, ReconciliationStatusPill) ──
//
// Lighter bg-50 + ring style. Used on page surfaces where chips sit alone
// rather than packed into a dense table row.
export const CHIP_PILL = {
  HEALTHY:   'bg-green-50 text-green-700 ring-1 ring-green-200 dark:bg-green-900/20 dark:text-green-300 dark:ring-green-800',
  ATTENTION: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800',
  ERROR:     'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800',
  NEUTRAL:   'bg-gray-50 text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700',
  ACCENT:    'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800',
  HIGHLIGHT: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:ring-purple-800',
} as const;

// ── Semantic aliases used by the generic <Badge> component ───────────────
//
// Lets the Badge API stay variant-based ("success" / "danger") while the
// underlying classes come from CHIP_DENSE so a Badge with variant="success"
// is byte-identical to a chip rendered via CHIP_DENSE.COMPLETE.
export const BADGE_VARIANT = {
  default: CHIP_DENSE.CANCELLED,       // gray-100 / gray-600
  success: CHIP_DENSE.COMPLETE,        // green
  info:    CHIP_DENSE.OPEN,            // blue
  warning: CHIP_DENSE.TRIGGER_PENDING, // yellow
  danger:  CHIP_DENSE.REJECTED,        // red
} as const;

// ── Convenience type exports ─────────────────────────────────────────────
export type ChipDenseKind    = keyof typeof CHIP_DENSE;
export type ChipSideKind     = keyof typeof CHIP_SIDE;
export type ChipMetadataKind = keyof typeof CHIP_METADATA;
export type ChipAccentKind   = keyof typeof CHIP_ACCENT;
export type ChipCategoryKind = keyof typeof CHIP_CATEGORY;
export type ChipRiskKind     = keyof typeof CHIP_RISK;
export type ChipPillKind     = keyof typeof CHIP_PILL;
export type BadgeVariant     = keyof typeof BADGE_VARIANT;
